// Scroll position is the single source of truth: the section is pinned inside a
// runway of one viewport per tab, and a click just scrolls to the tab's slot.

((window, document) => {
    const section = document.querySelector('.js--tab-content')

    if (!section)
        return

    const pin = section.querySelector('.tab-content__pin')

    const links = Array.prototype.slice
        .call(section.querySelectorAll('.js--tab-content-link'))

    const panels = Array.prototype.slice
        .call(section.querySelectorAll('.js--tab-content-panel'))

    const frames = Array.prototype.slice
        .call(section.querySelectorAll('.js--tab-content-frame'))

    const count = links.length

    // colour blend and column parallax as the section arrives; aside lands first
    const blendSupported = Boolean(window.CSS && window.CSS.supports)
        && window.CSS.supports('color', 'color-mix(in srgb, red 50%, blue)')
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const leavingVisible = 0.3
    const asideTravel = 40
    const mediaTravel = 90
    const mediaLag = 0.12

    let blendTicking = false

    /**
    * Ease Out
    * @param  {Number} value 0 - 1
    * @return {Number}
    */
    function easeOut (value) {
        return (1 - Math.pow(1 - value, 3))
    }

    /**
    * Paint the Arrival for the Current Scroll Position
    * @return {Void}
    */
    function paintBlend () {
        blendTicking = false

        // starts when 30% of the section above is left on screen
        const above = section.previousElementSibling
        const start = (above ? above.offsetHeight : window.innerHeight)
            * leavingVisible

        const progress = Math.min(Math.max(
            1 - (section.getBoundingClientRect().top / start), 0), 1)

        const media = Math.min(Math.max(
            (progress - mediaLag) / (1 - mediaLag), 0), 1)

        section.style.setProperty(
            '--tab-content-blend', progress.toFixed(3))

        section.style.setProperty('--tab-content-aside-shift',
            `${((1 - easeOut(progress)) * asideTravel).toFixed(1)}px`)

        section.style.setProperty('--tab-content-media-shift',
            `${((1 - easeOut(media)) * mediaTravel).toFixed(1)}px`)
    }

    /**
    * Queue a Paint on the Next Frame
    * @return {Void}
    */
    function requestBlend () {
        if (blendTicking)
            return

        blendTicking = true

        window.requestAnimationFrame(paintBlend)
    }

    if (blendSupported) {
        section.classList.add('tab-content_blend')

        paintBlend()

        window
            .addEventListener('scroll',
                requestBlend)

        window
            .addEventListener('resize',
                requestBlend)
    }

    if (count < 2)
        return

    // matches $tab-content-duration in _tab-content.scss
    const duration = 900

    // ie11 keeps the plain click-only section. overflow-x: clip is what lets
    // sticky work at all here - see the note in _tab-content.scss
    const canPin = Boolean(window.matchMedia)
        && Boolean(window.CSS && window.CSS.supports)
        && window.CSS.supports('position', 'sticky')
        && window.CSS.supports('overflow-x', 'clip')
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const covered = Boolean(section.nextElementSibling)
        && section.nextElementSibling.classList.contains('js--cta')

    let current = 0
    let wiping = null
    let measured = 0

    /**
    * Height of One Tab's Slot
    * @return {Integer}
    */
    function slot () {
        // the pin is sized in dvh on mobile, which innerHeight only matches
        // while the browser chrome happens to be in the same state
        return Math.round(pin.getBoundingClientRect().height)
            || window.innerHeight
    }

    /**
    * Runway Height in Pixels
    * @return {Integer}
    */
    function runway () {
        return (count - 1) * slot()
    }

    /**
    * Paint a Tab as Selected
    * @param  {Integer} index
    * @return {Void}
    */
    function markLink (index) {
        links.forEach((link, i) => {
            const isActive = i === index

            link.classList
                .toggle('tab-content__nav-link_active', isActive)

            link.setAttribute('aria-selected', isActive
                ? 'true'
                : 'false')

            link.setAttribute('tabindex', isActive
                ? '0'
                : '-1')
        })
    }

    /**
    * Clear the Transition Classes
    * @return {Void}
    */
    function settle () {
        section.classList.remove('tab-content_wiping')

        frames.forEach((frame, i) => {
            frame.classList.remove('tab-content__frame_leaving')
            frame.classList.remove('tab-content__frame_entering')
            frame.classList.toggle('tab-content__frame_active', i === current)
        })

        panels.forEach((panel) => {
            panel.classList.remove('tab-content__panel_leaving')
        })

        wiping = null
    }

    /**
    * Switch to a Tab
    * @param  {Integer} index
    * @return {Void}
    */
    function select (index) {
        if (index === current || index < 0 || index > count - 1)
            return

        const from = current

        // a switch landing mid-wipe restarts from wherever the last one got to;
        // the reflow is what lets the animation play a second time
        if (wiping) {
            window.clearTimeout(wiping)
            settle()
            section.getBoundingClientRect()
        }

        current = index

        markLink(index)

        panels[from].classList.remove('tab-content__panel_active')
        panels[from].classList.add('tab-content__panel_leaving')
        panels[index].classList.add('tab-content__panel_active')

        frames[from].classList.remove('tab-content__frame_active')
        frames[from].classList.add('tab-content__frame_leaving')
        frames[index].classList.add('tab-content__frame_entering')

        section.classList.add('tab-content_wiping')

        wiping = window.setTimeout(settle, duration)
    }

    /**
    * Read the Tab Out of the Scroll Position
    * @return {Void}
    */
    function readScroll () {
        // a mobile toolbar sliding away changes dvh, and so the slot, without
        // a resize event to go with it
        if (slot() !== measured)
            measure()

        const bounds = section.getBoundingClientRect()
        const travelled = Math.min(Math.max(-bounds.top, 0), runway())

        // the tab changes on crossing the middle of each slot of runway
        select(Math.min(
            Math.round(travelled / slot()),
            count - 1))
    }

    /**
    * Scroll a Tab's Slot to the Top of the Runway
    * @param  {Integer} index
    * @return {Void}
    */
    function scrollToTab (index) {
        const scrollTop = window.pageYOffset
            || document.documentElement.scrollTop
            || 0

        const top = section.getBoundingClientRect().top + scrollTop

        window.scrollTo({
            top: top + (index * slot()),
            behavior: 'smooth'
        })
    }

    /**
    * Size the Runway to the Pin
    * @return {Void}
    */
    function measure () {
        // the pin is viewport-sized, so this stays true whatever the runway
        // underneath it is currently set to
        measured = slot()

        // runway for the last tab before the cta covers it; shorter on phones
        const mobile = window.matchMedia('(max-width: 767px)').matches
        const hold = covered ? (mobile ? 0.5 : 1) : 0

        section.style.height = `${Math.round(measured * (count + hold))}px`
    }

    links.forEach((link, index) => {
        link
            .addEventListener('click', () => {
                if (canPin)
                    return scrollToTab(index)

                select(index)
            })

        // left/right walk the tablist, as the tab pattern expects
        link
            .addEventListener('keydown', (event) => {
                const step = (event.key === 'ArrowRight'
                        || event.key === 'ArrowDown')
                    ? 1
                    : (event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                        ? -1
                        : 0)

                if (!step)
                    return

                event.preventDefault()

                const next = Math.min(Math.max(index + step, 0), count - 1)

                links[next].focus()

                if (canPin)
                    return scrollToTab(next)

                select(next)
            })
    })

    if (!canPin)
        return

    document.documentElement.classList.add('tab-content-pinned')
    section.classList.add('tab-content_pinned')

    measure()

    // the pin is only as tall as the viewport, so a mid-section resize leaves
    // the runway measured against the old one
    window
        .addEventListener('resize', () => {
            measure()
            readScroll()
        })

    window
        .addEventListener('scroll',
            readScroll)

    // a reload restores the scroll position before this runs
    readScroll()

    // focusing a tab scrolls the pin out of the sticky range in some browsers
    pin
        .addEventListener('scroll', () => {
            if (pin.scrollTop)
                pin.scrollTop = 0
        })
})(window, document)
